import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { FloatCardComponent } from "../../components/cards";
import SellerProductsTableComponent from "../../components/tables/Seller/SellerProductsTableComponent";
import PageLayout from "../../layouts/PageLayout";

export default function SellerProductListPage() {
    const { t, n } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Tabs: 'new' | 'approved' | 'dispatched'
    const [activeTab, setActiveTab] = useState("new");
    const [allProducts, setAllProducts] = useState([]);

    const [counts, setCounts] = useState({
        new: 0,
        approved: 0,
        dispatched: 0,
    });

    const tableHeaders = [
        "Product Code",
        "Title",
        "Category",
        "Brand",
        "Condition",
        "Buying Price",
        "Quantity",
        "Action",
    ];

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser")}`,
        },
    });

    const extractData = (res) => {
        if (res?.data?.data && Array.isArray(res.data.data)) return res.data.data;
        if (Array.isArray(res?.data)) return res.data;
        return [];
    };

    const fetchCounts = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_URL;
            const [newRes, approvedRes, dispatchedRes] = await Promise.all([
                axios.get(`${baseUrl}/store-seller/new-products`, getAuthHeader()),
                axios.get(`${baseUrl}/store-seller/approved-products`, getAuthHeader()),
                axios.get(`${baseUrl}/store-seller/dispatched-products`, getAuthHeader()),
            ]);

            setCounts({
                new: extractData(newRes).length,
                approved: extractData(approvedRes).length,
                dispatched: extractData(dispatchedRes).length,
            });
        } catch (err) {
            console.error("Error fetching counts:", err);
        }
    }, []);

    const fetchTabData = useCallback(async (tab) => {
        setLoading(true);
        setError(null);

        let endpoint = "";
        if (tab === "new") endpoint = "/store-seller/new-products";
        else if (tab === "approved") endpoint = "/store-seller/approved-products";
        else if (tab === "dispatched") endpoint = "/store-seller/dispatched-products";

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}${endpoint}`,
                getAuthHeader()
            );
            setAllProducts(extractData(response));
        } catch (err) {
            console.error("Error fetching tab data:", err);
            setError(
                err.response?.data?.message || "Failed to fetch products. Please try again."
            );
            setAllProducts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    useEffect(() => {
        fetchTabData(activeTab);
    }, [activeTab, fetchTabData]);

    const cardsData = [
        {
            key: "new",
            title: "Fresh Products",
            digit: counts.new,
            icon: "shopping_bag",
            variant: activeTab === "new" ? "lg blue active-card" : "lg blue",
        },
        {
            key: "approved",
            title: "Approved Products",
            digit: counts.approved,
            icon: "verified_user",
            variant: activeTab === "approved" ? "lg green active-card" : "lg green",
        },
        {
            key: "dispatched",
            title: "Dispatched Products",
            digit: counts.dispatched,
            icon: "local_shipping",
            variant: activeTab === "dispatched" ? "lg purple active-card" : "lg purple",
        },
    ];

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">
                                {t("seller_products")} ({allProducts.length})
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t("seller_products")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {cardsData.map((card) => (
                    <Col key={card.key} sm={6} lg={4}>
                        <div style={{ cursor: "pointer" }} onClick={() => setActiveTab(card.key)}>
                            <FloatCardComponent
                                variant={card.variant}
                                digit={n ? n(card.digit) : card.digit}
                                title={card.title}
                                icon={card.icon}
                            />
                        </div>
                    </Col>
                ))}

                <Col xl={12}>
                    <div className="mc-card">
                        {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("loading_products...")}</p>
                            </div>
                        ) : (
                            <SellerProductsTableComponent
                                thead={tableHeaders}
                                tbody={allProducts}
                                activeTab={activeTab}
                            />
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}